import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiUpload,
  FiTrash2,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import bankService from "../../../services/bank.service";
import PaginationBar from "../../../components/common/PaginationBar";
import DownloadSampleExcelButton from "../../../components/common/DownloadSampleExcelButton";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readPagination } from "../../../utils/listPagination";
import { useBankExcelUpload } from "../../../hooks/useBankExcelUpload";
import { formatUploadProgressHint } from "../../../utils/uploadRowCount";
import "../../../styles/users.css";
import "../../../styles/bankRecordDetail.css";

function UploadProgressBar({ percent }) {
  return (
    <div
      style={{
        margin: "12px 0",
        background: "#e5e7eb",
        borderRadius: 6,
        overflow: "hidden",
        height: 10,
      }}
    >
      <div
        style={{
          width: `${percent}%`,
          background: "var(--accent,#2563eb)",
          height: "100%",
          transition: "width 0.3s",
        }}
      />
    </div>
  );
}

function statusBadge(status) {
  if (status === "completed") return { cls: "active", label: "Completed" };
  if (status === "processing" || status === "pending") return { cls: "pending", label: "Processing" };
  if (status === "failed") return { cls: "failed", label: "Failed" };
  return { cls: "expired", label: status || "—" };
}

function BatchStatusCard({ batch, onDismiss }) {
  if (!batch) return null;
  const isProcessing = batch.status === "processing" || batch.status === "pending";
  const isFailed = batch.status === "failed";
  const isDone = batch.status === "completed";
  const processed = Number(batch.processedRows) || 0;
  const total = Number(batch.totalRows) || 0;
  const pct = isDone
    ? 100
    : total > 0
      ? Math.min(100, Math.round((processed / total) * 100))
      : null;
  const hint = formatUploadProgressHint({
    processedRows: processed,
    totalRows: total,
    message: batch.importNote,
  });

  return (
    <div
      className="bank-files-status-card"
      style={{
        padding: "14px 18px",
        borderRadius: 8,
        border: `1px solid ${isDone ? "#bbf7d0" : isFailed ? "#fecaca" : "#bfdbfe"}`,
        background: isDone ? "#f0fdf4" : isFailed ? "#fef2f2" : "#eff6ff",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isProcessing && <FiLoader size={16} className="bank-files-spin" />}
        {isDone && <FiCheckCircle size={16} color="#16a34a" />}
        {isFailed && <FiAlertCircle size={16} color="#dc2626" />}
        <strong style={{ fontSize: 14 }}>
          {isProcessing ? "Processing upload…" : isDone ? "Upload complete" : "Upload failed"}
        </strong>
        {!isProcessing && (
          <button
            type="button"
            onClick={onDismiss}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              color: "#6b7280",
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>
      {isProcessing && (
        <>
          {pct != null ? <UploadProgressBar percent={pct} /> : null}
          <p style={{ fontSize: 12, color: "#4b5563", margin: "8px 0 0" }}>
            {hint || "Importing rows…"}
            {pct != null ? ` (${pct}%)` : ""}
          </p>
          <p style={{ fontSize: 12, color: "#4b5563", margin: "4px 0 0" }}>
            Imported {Number(batch.successRows || 0).toLocaleString()}
            {Number(batch.failedRows) > 0 ? ` · ${Number(batch.failedRows).toLocaleString()} skipped` : ""}
            {Number(batch.duplicateRows) > 0
              ? ` · ${Number(batch.duplicateRows).toLocaleString()} duplicates`
              : ""}
          </p>
        </>
      )}
      {isDone && (
        <p style={{ fontSize: 13, color: "#166534", marginTop: 8, marginBottom: 0 }}>
          {Number(batch.successRows || 0).toLocaleString()} rows imported
          {Number(batch.failedRows) > 0 ? ` · ${Number(batch.failedRows).toLocaleString()} skipped` : ""}
          {Number(batch.duplicateRows) > 0
            ? ` · ${Number(batch.duplicateRows).toLocaleString()} duplicates`
            : ""}
          {total > 0 ? ` · ${processed.toLocaleString()} / ${total.toLocaleString()} (100%)` : ""}
        </p>
      )}
      {isFailed && (
        <p style={{ fontSize: 13, color: "#991b1b", marginTop: 8, marginBottom: 0 }}>
          {batch.errorMessage || "Processing failed"}
        </p>
      )}
    </div>
  );
}

export default function BankUploadFiles() {
  const { auth } = useAuth();
  const isAdmin = auth?.user?.role === "BANK_ADMIN";
  const fileRef = useRef(null);

  const [batches, setBatches] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bankService.listUploads({ page, limit: PAGE_LIMIT });
      setBatches(res?.data?.data?.batches || []);
      setPagination(readPagination(res?.data));
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const {
    uploadStage,
    uploadProgress,
    uploadError,
    setUploadError,
    activeBatch,
    dismissBatch,
    uploadFile,
    uploadButtonLabel,
    isBusy,
  } = useBankExcelUpload({
    onComplete: () => loadBatches(),
  });

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (file) uploadFile(file);
  };

  const handleDeleteBatch = async (batch) => {
    if (!batch?._id) return;
    const rows = batch.successRows ?? 0;
    if (
      !window.confirm(
        `Delete file "${batch.fileName}" and all ${rows > 0 ? rows.toLocaleString() : ""} imported row(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingBatchId(batch._id);
    try {
      const res = await bankService.deleteBatchRecords(batch._id);
      const data = res?.data?.data || {};
      const n = Number(data.deleted) || 0;
      const removed = data.batchRemoved !== false;
      const msg = res?.data?.message;
      if (removed) {
        window.alert(
          n > 0
            ? `${n.toLocaleString()} case(s) deleted from your bank / NBFC and from all linked repo agencies.\n\n${msg || ""}`
            : "Upload file removed from your list."
        );
      } else {
        window.alert(msg || "Delete completed.");
      }
      await loadBatches();
      if (activeBatch?._id === batch._id) dismissBatch();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        (status === 403
          ? "You can only delete files you uploaded."
          : "Could not delete file. Try again or contact support.");
      window.alert(msg);
    } finally {
      setDeletingBatchId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="page bank-files-page">
      <div className="bank-files-page__header">
        <div>
          <h2>Upload Repossession List</h2>
          <p className="muted" style={{ fontSize: 14, marginTop: 4 }}>
            All Excel files you have uploaded. Delete a file to remove its imported rows.
            {isAdmin ? " Bank / NBFC admins see every upload in the bank / NBFC." : " You only see your own files."}
          </p>
        </div>
        <div className="bank-files-page__actions">
          <DownloadSampleExcelButton
            showFieldSelect={false}
            className="bank-files-page__sample"
            label="Download Sample Excel"
          />
          <button
            type="button"
            className="primary-page-btn"
            disabled={isBusy}
            onClick={() => fileRef.current?.click()}
          >
            <FiUpload style={{ marginRight: 6 }} />
            {uploadButtonLabel()}
          </button>
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
      </div>

      {uploadStage === "uploading" && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, marginBottom: 4 }}>Uploading to storage… {uploadProgress}%</p>
          <UploadProgressBar percent={uploadProgress} />
        </div>
      )}

      <BatchStatusCard batch={activeBatch} onDismiss={dismissBatch} />

      {uploadError && (
        <div className="bank-files-error">
          <p>{uploadError}</p>
          <button type="button" onClick={() => setUploadError("")} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <p className="muted bank-files-page__hint">
        Large files (400k+ rows) are processed in the background.
      </p>

      <div className="company-table-wrap">
        <table className="users-table excel-grid-table">
          <thead>
            <tr>
              <th>File name</th>
              <th>Uploaded</th>
              <th>Uploaded by</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Rows imported</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>Loading files…</td>
              </tr>
            ) : batches.length === 0 ? (
              <tr>
                <td colSpan={8}>No records found</td>
              </tr>
            ) : (
              batches.map((batch) => {
                const badge = statusBadge(batch.status);
                return (
                  <tr key={batch._id}>
                    <td>
                      <div className="bank-files-table__name">
                        <FiFileText aria-hidden />
                        <span title={batch.fileName}>{batch.fileName}</span>
                      </div>
                    </td>
                    <td>{formatDate(batch.createdAt)}</td>
                    <td>{batch.uploadedBy?.name || "—"}</td>
                    <td>{batch.uploadedBy?.email || "—"}</td>
                    <td>{batch.uploadedBy?.phone || "—"}</td>
                    <td>
                      {batch.successRows != null
                        ? batch.successRows.toLocaleString("en-IN")
                        : "—"}
                    </td>
                    <td>
                      <span className={`company-status company-status--${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="company-actions-td">
                      <div className="company-actions-cell">
                        <button
                          type="button"
                          className="company-btn company-btn--delete"
                          disabled={deletingBatchId === batch._id || batch.status === "processing"}
                          onClick={() => handleDeleteBatch(batch)}
                          title={
                            batch.status === "processing"
                              ? "Wait until processing finishes"
                              : "Delete file and all imported rows"
                          }
                        >
                          <FiTrash2 aria-hidden />
                          <span>
                            {deletingBatchId === batch._id ? "Deleting…" : "Delete"}
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />

      <style>{`
        .bank-files-spin { animation: bank-files-spin 1s linear infinite; }
        @keyframes bank-files-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
